import { z } from 'zod';
import {
    HomeAssetGroupDistributionSchema,
    HomeExecutiveSummarySchema,
    HomeMetricsSchema,
    HomePageDataSchema,
    HomeTicketSummarySchema,
} from './home.schema.js';

/**
 * Widget data-section contract.
 *
 * `dataSection` on a widget definition (`@repo/widgets`) must match one of
 * these keys; the provider layer resolves them against the backing store /
 * API. The home summary endpoint (`GET /home?sections=a,b`) computes only
 * the requested sections.
 */
export const WIDGET_HOME_SECTIONS = Object.freeze([
    'metrics',
    'assetGroupDistribution',
    'ticketSummary',
    'executiveSummary',
]);

/** Sections served by `GET /risk/monitoring?sections=...`. */
export const WIDGET_RISK_MONITORING_SECTIONS = Object.freeze([
    'riskRegisterStats',
    'riskTreatmentStats',
]);

/**
 * Domain stats endpoints used when a catalog widget is dropped on a dashboard.
 * Page strips still pass `data` explicitly; the provider stays passive then.
 */
export const WIDGET_PAGE_SECTIONS = Object.freeze([
    'biaStats',
    'bcpStats',
    'exerciseStats',
    'crisisStats',
    'unitChartStats',
    'assetStats',
    'incidentStats',
    'gdprDashboardStats',
    'vendorStats',
    'assetLicenseStats',
    'assetDisposalStats',
    'kpiDashboardStats',
    'soaStats',
    'trainingStats',
    'correctiveActionStats',
    'consentRecordStats',
    'systemLogStats',
    'itsmStats',
]);

export const WIDGET_ENTITY_SECTIONS = Object.freeze([
    'riskEntity',
    'assetEntityDependencies',
]);

export const WIDGET_DATA_SECTIONS = Object.freeze([
    ...WIDGET_HOME_SECTIONS,
    ...WIDGET_RISK_MONITORING_SECTIONS,
    ...WIDGET_PAGE_SECTIONS,
    ...WIDGET_ENTITY_SECTIONS,
]);

const NonnegativeNumberSchema = z.number().nonnegative();
const NonnegativeIntegerSchema = z.number().int().nonnegative();
const NullableDateTimeSchema = z.string().datetime({ offset: true }).nullable();

const ChartDatasetSchema = z.object({
    label: z.string().optional(),
    data: z.array(z.number()),
    backgroundColor: z.union([z.string(), z.array(z.string())]).optional(),
    borderColor: z.union([z.string(), z.array(z.string())]).optional(),
    borderWidth: z.number().optional(),
    themeColorIndexes: z.array(NonnegativeIntegerSchema).optional(),
});

const ChartLegendItemSchema = z.object({
    label: z.string(),
    value: z.union([z.number(), z.string()]),
    color: z.string().optional(),
    metaKey: z.string().optional(),
    themeColorIndex: NonnegativeIntegerSchema.optional(),
});

const ChartDataSchema = z.object({
    labels: z.array(z.string()),
    datasets: z.array(ChartDatasetSchema).min(1),
});

/** Raw chart payload returned by BCM statistics endpoints. */
export const WidgetRawChartSchema = ChartDataSchema.extend({
    items: z.array(ChartLegendItemSchema).optional(),
});

/** Normalized chart payload consumed by risk widgets. */
export const WidgetChartBlockSchema = z.object({
    chartData: ChartDataSchema,
    legendItems: z.array(ChartLegendItemSchema),
    total: NonnegativeNumberSchema,
}).nullable();

export const RiskRegisterStatsSectionSchema = z.object({
    totalRisks: NonnegativeNumberSchema,
    riskExposureTotal: NonnegativeNumberSchema,
    financialImpactTotal: NonnegativeNumberSchema,
    treatmentActivities: NonnegativeNumberSchema,
    treatmentProgress: NonnegativeNumberSchema,
    risksWithControls: NonnegativeNumberSchema,
    risksWithTreatments: NonnegativeNumberSchema,
    priorityDistribution: WidgetChartBlockSchema,
    responseStrategyDistribution: WidgetChartBlockSchema,
    inherentRisks: WidgetChartBlockSchema,
    residualRisks: WidgetChartBlockSchema,
});

export const RiskTreatmentStatsSectionSchema = z.object({
    totalTreatments: NonnegativeNumberSchema,
    treatmentProgress: NonnegativeNumberSchema,
    overdueCount: NonnegativeNumberSchema,
    dueSoonCount: NonnegativeNumberSchema,
    completedRecently: NonnegativeNumberSchema,
    budgetTotals: z.object({
        totalBudget: NonnegativeNumberSchema,
        avgBudget: NonnegativeNumberSchema,
        withBudgetCount: NonnegativeIntegerSchema,
    }),
    treatmentsByStatus: WidgetChartBlockSchema,
    treatmentsBudget: WidgetChartBlockSchema,
    treatmentsByDivision: WidgetChartBlockSchema,
    strategyBreakdown: WidgetChartBlockSchema,
    topOwners: WidgetChartBlockSchema,
});

export const BiaStatsSectionSchema = z.object({
    totalBia: NonnegativeIntegerSchema,
    reviewWithin30Days: NonnegativeIntegerSchema,
    overdueBia: NonnegativeIntegerSchema,
    criticalCount: NonnegativeIntegerSchema,
    biaByCategory: WidgetRawChartSchema,
    biaByState: WidgetRawChartSchema,
    biaByRto: WidgetRawChartSchema,
    biaByUnit: WidgetRawChartSchema,
    biaByNextReview: WidgetRawChartSchema,
    biaByCriticality: WidgetRawChartSchema,
    highImpacts: WidgetRawChartSchema,
});

export const BcpStatsSectionSchema = z.object({
    totalBcp: NonnegativeIntegerSchema,
    overdueReviewCount: NonnegativeIntegerSchema,
    dueWithin7Count: NonnegativeIntegerSchema,
    dueBetween8And30Count: NonnegativeIntegerSchema,
    dueAfter30Count: NonnegativeIntegerSchema,
    noNextReviewCount: NonnegativeIntegerSchema,
    bcpByState: WidgetRawChartSchema.nullable(),
    bcpByStatus: WidgetRawChartSchema.nullable(),
    bcpByUnit: WidgetRawChartSchema.nullable(),
    bcpReviewBuckets: WidgetRawChartSchema.nullable(),
});

export const ExerciseStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    completed: NonnegativeIntegerSchema,
    planned: NonnegativeIntegerSchema,
    improvements: NonnegativeIntegerSchema,
    exercisesByStatus: WidgetRawChartSchema.nullable(),
    exercisesByType: WidgetRawChartSchema.nullable(),
});

export const CrisisStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    active: NonnegativeIntegerSchema,
    critical: NonnegativeIntegerSchema,
    ongoingRecovery: NonnegativeIntegerSchema,
    crisisByStatus: WidgetRawChartSchema.nullable(),
    crisisBySeverity: WidgetRawChartSchema.nullable(),
});

export const UnitChartStatsSectionSchema = z.array(z.object({
    unitId: z.string().min(1),
    name: z.string(),
    parent: z.string().nullable(),
    directCount: NonnegativeIntegerSchema,
    totalCount: NonnegativeIntegerSchema,
}));

export const AssetStatsSectionSchema = z.object({
    totalAssets: NonnegativeNumberSchema,
    activeAssets: NonnegativeNumberSchema,
    inactiveAssets: NonnegativeNumberSchema,
    institutionApproved: NonnegativeNumberSchema,
    institutionNotApproved: NonnegativeNumberSchema,
    totalCurrentValue: NonnegativeNumberSchema,
    byCategory: z.record(z.string(), NonnegativeNumberSchema),
    byHosting: z.record(z.string(), NonnegativeNumberSchema),
});

export const IncidentStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    open: NonnegativeIntegerSchema,
    critical: NonnegativeIntegerSchema,
    statusDistribution: z.record(z.string(), NonnegativeIntegerSchema),
    severityDistribution: z.record(z.string(), NonnegativeIntegerSchema),
    trend: z.array(z.object({
        year: z.number().int(),
        month: z.number().int().min(1).max(12),
        count: NonnegativeIntegerSchema,
    })),
});

export const GdprDashboardStatsSectionSchema = z.object({
    dsr: z.object({
        total: NonnegativeIntegerSchema,
        open: NonnegativeIntegerSchema,
        overdue: NonnegativeIntegerSchema,
        dueSoon: NonnegativeIntegerSchema,
    }),
    breach: z.object({
        total: NonnegativeIntegerSchema,
        open: NonnegativeIntegerSchema,
        overdue72h: NonnegativeIntegerSchema,
    }),
    notices: z.object({
        total: NonnegativeIntegerSchema,
        published: NonnegativeIntegerSchema,
        needsReview: NonnegativeIntegerSchema,
    }),
    dpia: z.object({
        total: NonnegativeIntegerSchema,
        inProgress: NonnegativeIntegerSchema,
        highResidualRisk: NonnegativeIntegerSchema,
    }),
    disposal: z.object({
        openRuns: NonnegativeIntegerSchema,
        plannedRecords: NonnegativeIntegerSchema,
        lastCompletedAt: NullableDateTimeSchema,
    }),
    dpa: z.object({
        active: NonnegativeIntegerSchema,
        expiringSoon: NonnegativeIntegerSchema,
        expired: NonnegativeIntegerSchema,
    }),
    consent: z.object({
        valid: NonnegativeIntegerSchema,
        withdrawn: NonnegativeIntegerSchema,
    }),
    inventory: z.object({
        total: NonnegativeIntegerSchema,
        missingRetention: NonnegativeIntegerSchema,
    }),
    ropa: z.object({ activityCount: NonnegativeIntegerSchema }),
    cookies: z.object({
        total: NonnegativeIntegerSchema,
        consentRequired: NonnegativeIntegerSchema,
    }),
    training: z.object({
        assigned: NonnegativeIntegerSchema,
        completed: NonnegativeIntegerSchema,
    }),
    verbis: z.object({
        pending: NonnegativeIntegerSchema,
        overdue: NonnegativeIntegerSchema,
        lastSyncAt: NullableDateTimeSchema,
    }),
});

export const VendorStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    active: NonnegativeIntegerSchema,
    criticalityHigh: NonnegativeIntegerSchema,
    criticalityMedium: NonnegativeIntegerSchema,
    criticalityLow: NonnegativeIntegerSchema,
    contractExpiringWithin30Days: NonnegativeIntegerSchema,
    contractExpired: NonnegativeIntegerSchema,
    withLinkedAssets: NonnegativeIntegerSchema,
    assessmentsDueWithin30Days: NonnegativeIntegerSchema,
    assessmentAvgOverallScore: NonnegativeNumberSchema.nullable(),
    assessmentsOpenMissingSurvey: NonnegativeIntegerSchema,
    assessmentsClosedLast90Days: NonnegativeIntegerSchema,
});

export const AssetLicenseStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    active: NonnegativeIntegerSchema,
    expiringWithin30Days: NonnegativeIntegerSchema,
    activeButEndDatePassed: NonnegativeIntegerSchema,
    activeWithoutEndDate: NonnegativeIntegerSchema,
    seatCapExceeded: NonnegativeIntegerSchema,
    upcomingRenewalBudget: NonnegativeNumberSchema,
    unusedSeatCostEstimate: NonnegativeNumberSchema,
});

export const AssetDisposalStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    planned: NonnegativeIntegerSchema,
    inProgress: NonnegativeIntegerSchema,
    completed: NonnegativeIntegerSchema,
    certified: NonnegativeIntegerSchema,
    plannedNext30Days: NonnegativeIntegerSchema,
    overduePlanned: NonnegativeIntegerSchema,
});

export const KpiDashboardStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    active: NonnegativeIntegerSchema,
    avgProgress: z.number().min(0).max(100),
    byStatus: z.array(z.object({ _id: z.string().nullable(), count: NonnegativeIntegerSchema })).optional(),
    byCategory: z.array(z.object({ _id: z.string().nullable(), count: NonnegativeIntegerSchema })).optional(),
});

export const SoaStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    not_started: NonnegativeIntegerSchema,
    in_progress: NonnegativeIntegerSchema,
    complete: NonnegativeIntegerSchema,
});

export const TrainingStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    published: NonnegativeIntegerSchema,
    mandatory: NonnegativeIntegerSchema,
    byType: z.record(z.string(), NonnegativeIntegerSchema),
});

export const CorrectiveActionStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    open: NonnegativeIntegerSchema,
    completed: NonnegativeIntegerSchema,
    delayed: NonnegativeIntegerSchema,
    byType: z.object({
        corrective: NonnegativeIntegerSchema,
        preventive: NonnegativeIntegerSchema,
        improvement: NonnegativeIntegerSchema,
    }),
});

export const ConsentRecordStatsSectionSchema = z.object({
    total: NonnegativeIntegerSchema,
    valid: NonnegativeIntegerSchema,
    withdrawn: NonnegativeIntegerSchema,
    expired: NonnegativeIntegerSchema,
});

const NamedCountSchema = z.object({
    count: NonnegativeIntegerSchema,
}).passthrough();

export const SystemLogStatsSectionSchema = z.object({
    totalLogs: NonnegativeIntegerSchema,
    errorRate: z.union([z.number(), z.string()]),
    actionCounts: z.array(NamedCountSchema),
    topUsers: z.array(NamedCountSchema),
    hourlyDistribution: z.array(NamedCountSchema),
    severityCounts: z.array(NamedCountSchema),
    categoryCounts: z.array(NamedCountSchema),
    statusCounts: z.array(NamedCountSchema),
});

export const ItsmStatsSectionSchema = z.object({
    openTickets: NonnegativeIntegerSchema,
    openIncidents: NonnegativeIntegerSchema,
    openProblems: NonnegativeIntegerSchema,
    openChanges: NonnegativeIntegerSchema,
    openProblemCandidates: NonnegativeIntegerSchema,
});

export const RiskEntitySectionSchema = z.object({
    scoreHistory: z.array(z.object({
        date: z.union([z.string(), z.date()]),
        score: z.number().optional(),
        residualScore: z.number().optional(),
    })).optional(),
    historyLog: z.array(z.object({
        date: z.union([z.string(), z.date()]),
        score: z.number().optional(),
        residualScore: z.number().optional(),
    })).optional(),
}).passthrough();

export const AssetEntityDependenciesSectionSchema = z.object({
    grcLinks: z.record(z.string(), NonnegativeIntegerSchema).optional(),
    operationalLinks: z.record(z.string(), NonnegativeIntegerSchema).optional(),
    relatedRisks: z.array(z.object({
        priority: z.string().optional(),
    }).passthrough()).optional(),
}).passthrough();

/** Schema lookup used by widget providers and correctness checks. */
export const WIDGET_SECTION_SCHEMAS = Object.freeze({
    metrics: HomeMetricsSchema,
    assetGroupDistribution: HomeAssetGroupDistributionSchema,
    ticketSummary: HomeTicketSummarySchema,
    executiveSummary: HomeExecutiveSummarySchema,
    riskRegisterStats: RiskRegisterStatsSectionSchema,
    riskTreatmentStats: RiskTreatmentStatsSectionSchema,
    biaStats: BiaStatsSectionSchema,
    bcpStats: BcpStatsSectionSchema,
    exerciseStats: ExerciseStatsSectionSchema,
    crisisStats: CrisisStatsSectionSchema,
    unitChartStats: UnitChartStatsSectionSchema,
    assetStats: AssetStatsSectionSchema,
    incidentStats: IncidentStatsSectionSchema,
    gdprDashboardStats: GdprDashboardStatsSectionSchema,
    vendorStats: VendorStatsSectionSchema,
    assetLicenseStats: AssetLicenseStatsSectionSchema,
    assetDisposalStats: AssetDisposalStatsSectionSchema,
    kpiDashboardStats: KpiDashboardStatsSectionSchema,
    soaStats: SoaStatsSectionSchema,
    trainingStats: TrainingStatsSectionSchema,
    correctiveActionStats: CorrectiveActionStatsSectionSchema,
    consentRecordStats: ConsentRecordStatsSectionSchema,
    systemLogStats: SystemLogStatsSectionSchema,
    itsmStats: ItsmStatsSectionSchema,
    riskEntity: RiskEntitySectionSchema,
    assetEntityDependencies: AssetEntityDependenciesSectionSchema,
});

/** Partial home payload: only the requested sections are present. */
export const HomeSectionsDataSchema = HomePageDataSchema.partial();

/**
 * Parse the `sections` query param (comma separated). Unknown keys are
 * dropped; empty / missing input means "all sections" (backwards-safe default).
 * @param {string | string[] | undefined} value
 * @returns {string[]}
 */
export function parseHomeSectionsParam(value) {
    const raw = Array.isArray(value) ? value.join(',') : value;
    if (raw == null || String(raw).trim() === '') return [...WIDGET_HOME_SECTIONS];
    const requested = String(raw)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    const valid = [...new Set(requested.filter((part) => WIDGET_HOME_SECTIONS.includes(part)))];
    return valid.length > 0 ? valid : [...WIDGET_HOME_SECTIONS];
}
